/**
 * Display-label layer for the admin console (MOT-96).
 *
 * Raw keys, ids and HTTP calls must not reach the UI as primary text. Each
 * helper here maps a raw value to the wording an admin should read, and every
 * one of them falls back to a humanized version of the raw value -- never to an
 * empty string -- so a key nobody has mapped yet still renders legibly.
 *
 * Pure module: no React, no data fetching.
 *
 * Note on `statusLabel` (components/ui/StatusChip): it lower-cases everything
 * after the first letter and returns 'Unknown' for blanks, which would mangle
 * already-cased values such as 'DJI' or 'Cameras'. `humanizeKey` below leaves
 * keys without separators untouched, so it is used instead.
 */

const hasOwn = (map: Record<string, string>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(map, key);

/**
 * Generic snake/kebab key to sentence case: `staged_product` becomes
 * `Staged product`. A value with no `_` or `-` separator is returned as-is
 * (first letter capitalised), so already-friendly text passes through.
 * Blank input returns `Unknown`, never an empty string.
 */
export const humanizeKey = (key: string | null | undefined): string => {
  const raw = String(key ?? '').trim();
  if (!raw) return 'Unknown';
  if (!/[_-]/.test(raw)) return raw.charAt(0).toUpperCase() + raw.slice(1);
  const spaced = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/** Like `humanizeKey` but Title Case, to match names such as "Market Pricing Refresh". */
export const titleCaseKey = (key: string | null | undefined): string =>
  humanizeKey(key)
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

/** Brand directory categories. */
export const CATEGORY_LABELS: Record<string, string> = {
  film_cameras: 'Film cameras',
  action_cameras: 'Action cameras',
  video_cameras: 'Video cameras',
};

export const categoryLabel = (key: string | null | undefined): string => {
  const raw = String(key ?? '').trim();
  if (raw && hasOwn(CATEGORY_LABELS, raw)) return CATEGORY_LABELS[raw];
  return humanizeKey(raw);
};

/**
 * Pipeline job keys (`pipelineRuns` document ids) that have no entry in the
 * Staged Products page's own PIPELINE_META table.
 */
export const PIPELINE_JOB_LABELS: Record<string, string> = {
  brand_dealer_directory_refresh: 'Brand & Dealer Directory Refresh',
  plan_drift_check: 'Plan Drift Check',
};

export const pipelineJobLabel = (key: string | null | undefined): string => {
  const raw = String(key ?? '').trim();
  if (raw && hasOwn(PIPELINE_JOB_LABELS, raw)) return PIPELINE_JOB_LABELS[raw];
  return titleCaseKey(raw);
};

/** Job `triggeredBy` keys. */
export const JOB_TRIGGER_LABELS: Record<string, string> = {
  remediation_dispatcher: 'Remediation dispatcher',
  api_connector: 'API connector',
};

/** A lower-case snake_case machine key, e.g. `remediation_dispatcher`. */
const SNAKE_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

/**
 * Friendly label for a job's `triggeredBy` value. A leading `system:` is
 * dropped. Only snake_case machine keys are humanized; anything else (a user
 * id, free text) is shown as given so it is never mangled. Blank is `Manual`.
 */
export const jobTriggerLabel = (raw: string | null | undefined): string => {
  const value = String(raw ?? '').trim();
  const key = value.startsWith('system:') ? value.slice('system:'.length).trim() : value;
  if (!key) return 'Manual';
  if (hasOwn(JOB_TRIGGER_LABELS, key)) return JOB_TRIGGER_LABELS[key];
  return SNAKE_KEY.test(key) ? humanizeKey(key) : key;
};

/** Audit log `resourceType` keys. */
export const AUDIT_RESOURCE_LABELS: Record<string, string> = {
  map_pricing: 'MAP pricing',
  staged_product: 'Staged product',
  canonical_product: 'Canonical product',
  pricing_ingestion: 'Pricing ingestion',
  admin: 'Admin',
};

export const auditResourceLabel = (key: string | null | undefined): string => {
  const raw = String(key ?? '').trim();
  if (raw && hasOwn(AUDIT_RESOURCE_LABELS, raw)) return AUDIT_RESOURCE_LABELS[raw];
  return humanizeKey(raw);
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** Strip query/hash, a leading `/api` and trailing slashes so rules match one shape. */
const normalizeAuditPath = (path: string | null | undefined): string =>
  String(path ?? '')
    .trim()
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/api(?=\/|$)/, '')
    .replace(/\/+$/, '');

interface AuditActionRule {
  methods: string[];
  pattern: RegExp;
  label: (match: RegExpMatchArray) => string;
}

/** First matching rule wins, so more specific paths go first. */
const AUDIT_ACTION_RULES: AuditActionRule[] = [
  {
    methods: ['POST'],
    pattern: /^\/map-pricing\/by-models$/,
    label: () => 'Looked up MAP pricing by models',
  },
  {
    methods: ['POST'],
    pattern: /^\/staged-products\/([^/]+)\/images\/prune-broken$/,
    label: (m) => `Pruned broken images · ${safeDecode(m[1])}`,
  },
  {
    methods: ['PUT', 'PATCH', 'POST'],
    pattern: /^\/admin\/feature-flags\/organizations\/([^/]+)\/([^/]+)$/,
    label: (m) => `Updated org feature flag: ${humanizeKey(safeDecode(m[2]))}`,
  },
  {
    methods: ['DELETE'],
    pattern: /^\/admin\/feature-flags\/organizations\/([^/]+)\/([^/]+)$/,
    label: (m) => `Cleared org feature flag: ${humanizeKey(safeDecode(m[2]))}`,
  },
  {
    methods: ['PUT', 'PATCH', 'POST'],
    pattern: /^\/admin\/feature-flags\/([^/]+)$/,
    label: (m) => `Updated feature flag: ${humanizeKey(safeDecode(m[1]))}`,
  },
];

/**
 * Human wording for an audit log HTTP call, e.g. `POST /api/map-pricing/by-models`
 * becomes `Looked up MAP pricing by models`. Pattern rules cover the known
 * routes; anything else falls back to `rawAction` (the stored action text),
 * then to `METHOD path`, so the raw call is always still visible.
 */
export const auditActionLabel = (
  method: string | null | undefined,
  path: string | null | undefined,
  rawAction?: string | null
): string => {
  const verb = String(method ?? '').trim().toUpperCase();
  const normalized = normalizeAuditPath(path);
  for (const rule of AUDIT_ACTION_RULES) {
    if (!rule.methods.includes(verb)) continue;
    const match = normalized.match(rule.pattern);
    if (match) return rule.label(match);
  }
  const fallback = String(rawAction ?? '').trim();
  if (fallback) return fallback;
  const rawPath = String(path ?? '').trim();
  return [verb, rawPath].filter(Boolean).join(' ') || 'Unknown action';
};
